import React, {useEffect, useContext} from 'react'
import { getOperation, updateOperation, getTasksForOp } from '../api/operations';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import SimpleTasksList from '../components/operations/SimpleTasksList';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import MuiLink from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, useParams } from 'react-router-dom';
import { sortTasks } from '../helpers/tasks';
import { name_for_op } from '../helpers/ops_names';
import { Button } from '@mui/material';
import { setAlert } from '../helpers/alerts';
import {WebSocketContext} from '../api/socket';
import GHAWorkflowOperation from '../components/operations/GHAWorkflowOperation';
import ArgoCDDeployOperation from '../components/operations/ArgoCDDeployOperation';
import { parseApiDate } from '../helpers/dates';

const CONCLUSION_COLORS = {
    success: 'success',
    failure: 'error',
    cancelled: 'default',
    skipped: 'default',
    timed_out: 'warning',
};

const OP_STATUS_COLORS = {
    completed: 'success',
    running: 'warning',
    pending: 'info',
    errored: 'error',
    cancelled: 'default',
    paused: 'default',
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return parseApiDate(iso).toLocaleString();
};

const AdminOps = (props) => {
  const { user } = props
  const { op_id } = useParams();
  const ws = useContext(WebSocketContext);
  const [operation, setOperation] = React.useState({loaded: false, data: {}});
  const [tasks, setTasks] = React.useState({loaded: false, data: []});

  useEffect(() => {
    if (!operation.loaded) {
        reloadOp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const reloadOp = () => {
    getOperation(op_id).then((r) => {
        setOperation({loaded: true, data: r});
        reloadTasks();
    }).catch((e) => {
        console.log("Error getting operations: ", e);
        setOperation({loaded: true, data: {}, error: e});
    });
  };

  const reloadTasks = () => {
    getTasksForOp(op_id).then((r) => {
        setTasks({loaded: true, data: r.tasks.sort(sortTasks)});
    }).catch((e) => {
        console.log("Error getting tasks: ", e);
        setTasks({loaded: true, data: [], error: e});
    });
 };

  const onWsOperationUpdate = (update) => {
    let op = update.operation;
    if (op.uuid !== op_id) return;
    setOperation(state_op => ({...state_op, data: op}));
  };

  const onWsTaskUpdate = (data) => {
    let task = data.task;
    if (task.operation_uuid !== op_id) return;
    setTasks(state_tasks => {
        let tasks = state_tasks.data;
        let idx = tasks.findIndex((t) => t.uuid === data.task.uuid);
        if (idx === -1) {
            tasks.push(data.task);
        } else {
            tasks[idx] = data.task;
        }
        return {...state_tasks, data: tasks.sort(sortTasks)};
    });
  };

  useEffect(() => {
    if (ws === null) return;
    ws.on('operation_update', onWsOperationUpdate);
    ws.on('task_update', onWsTaskUpdate);
    return () => {
        ws.off('operation_update');
        ws.off('task_update');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  const actOnOperation = (action) => {
    updateOperation(op_id, action).then((r) => {
        if (action === "delete") {
            setAlert("delete", `Deleted operation "${r.operation.uuid}"`, "success");
            window.location.href = "/ops";
            return;
        }
        reloadOp();
    }).catch((e) => {
        setAlert("cancel", e, "danger");
    });
  }

  const op = operation.data;
  const op_type = op.operation;
  const isGHA = op_type === 'gha_workflow' || op_type === 'fake_workflow' || op_type === 'aut';
  const isArgoCD = op_type === 'argocd_deploy';

  return (
    <Grid container spacing={4} justifyContent="center">

        {/* Operation summary */}
        <Grid size={{ xs: 12, md: 9 }}>
            <Paper sx={{ p: 2, minHeight: 183 }}>
                <Typography variant="h3" mb={1}>{name_for_op(op_type)}</Typography>
                {op.status && (
                    <Chip
                        label={op.status}
                        color={OP_STATUS_COLORS[op.status] || 'default'}
                        sx={{ mb: 2 }}
                    />
                )}
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                    {op.metadata?.step && (
                        <Typography variant="body1">
                            <strong>Step:</strong> {op.metadata.step}
                        </Typography>
                    )}
                    {op.metadata?.version && (
                        <Typography variant="body1">
                            <strong>Release:</strong>{' '}
                            <Link to={`/releases/${op.metadata.version}`} style={{ color: 'inherit' }}>
                                {op.metadata.version}
                            </Link>
                        </Typography>
                    )}
                    <Typography variant="body1">
                        <strong>Started:</strong> {formatDate(op.start_date)}
                    </Typography>
                    {op.end_date && (
                        <Typography variant="body1">
                            <strong>Completed:</strong> {formatDate(op.end_date)}
                        </Typography>
                    )}
                    {op.result?.conclusion && (
                        <Typography variant="body1">
                            <strong>Conclusion:</strong>{' '}
                            <Chip label={op.result.conclusion} size="small" color={CONCLUSION_COLORS[op.result.conclusion] || 'default'} />
                        </Typography>
                    )}
                    {op.result?.url && (
                        <Typography variant="body1">
                            <strong>Run:</strong>{' '}
                            <MuiLink href={op.result.url} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {op.operation === 'vulnerability_scan' ? 'Inspector findings' : 'Workflow run'} <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                            </MuiLink>
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {op_id}
                    </Typography>
                </Stack>
            </Paper>
        </Grid>

        {/* Actions */}
        <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 2, minHeight: 183 }}>
                <Typography variant="h3" mb={2}>Actions</Typography>
                <Stack spacing={1}>
                    <Button variant="contained" color="secondary" size="small" onClick={reloadOp}>
                        Reload
                    </Button>
                    {(op.status === 'errored' || op.status === 'running' || op.status === 'paused') && (
                        <Button variant="contained" color="warning" size="small" onClick={() => actOnOperation("cancel")}>
                            Cancel
                        </Button>
                    )}
                    {op.status === 'cancelled' && (
                        <Button variant="contained" color="error" size="small" onClick={() => actOnOperation("delete")}>
                            Delete
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Grid>

        {/* Operation-type specific detail */}
        {(isGHA || isArgoCD) && (
            <Grid size={{ xs: 12 }}>
                {isGHA ? <GHAWorkflowOperation operation={op} /> : <ArgoCDDeployOperation operation={op} />}
            </Grid>
        )}

        {/* Tasks */}
        <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch', minHeight: 183 }}>
                <Typography variant="h3" mb={1}>Tasks</Typography>
                <SimpleTasksList
                    tasks={tasks.data}
                    loaded={tasks.loaded}
                    reloadData={reloadTasks}
                />
            </Paper>
        </Grid>

    </Grid>
  )
}

export default AdminOps;
